import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Target, Shield, BookOpen, Handshake } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { SectionHeading } from '../components/SectionHeading';
import { CTASection } from '../components/CTASection';
import { PageHero } from '../components/PageHero';
export function OverOns() {
  return <div className="w-full" data-id="element-616">
      <PageHero title={<>
            Over <span className="font-medium" data-id="element-618">Waardebepaling.nl</span>
          </>} subtitle="Wij zijn een netwerk van onafhankelijke waarderingsspecialisten. Onze missie is om MKB-ondernemers te voorzien van helder, objectief en strategisch inzicht." data-id="element-617" />

      <section className="py-24 bg-white" data-id="element-619">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-620">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-621">
            <AnimatedSection data-id="element-622">
              <SectionHeading title="Onze Missie" className="mb-6" data-id="element-623" />
              <div className="prose prose-lg text-charcoal/70" data-id="element-624">
                <p className="mb-4" data-id="element-625">
                  Voor veel ondernemers is hun bedrijf hun pensioen, hun
                  levenswerk en hun grootste bezit. Toch weten de meesten niet
                  precies wat het waard is. Wij geloven dat elke ondernemer
                  recht heeft op een eerlijke, transparante en professionele{' '}
                  <Link to="/bedrijfswaardering" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-626">
                    bedrijfswaardering
                  </Link>
                  .
                </p>
                <p data-id="element-627">
                  Onze missie is om complexe financiële theorieën te vertalen
                  naar begrijpelijke rapportages. We bieden strategisch inzicht
                  dat u helpt bij cruciale beslissingen, of het nu gaat om een
                  verkoop, overname of een conflict. Lees meer over{' '}
                  <Link to="/kennisbank/bedrijfswaardering/wat-is-een-bedrijfswaardering" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-628">
                    wat een bedrijfswaardering precies inhoudt
                  </Link>
                  .
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2} data-id="element-629">
              <div className="rounded-2xl overflow-hidden shadow-lg h-[400px]" data-id="element-630">
                <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1000" alt="Team overleg" className="w-full h-full object-cover" data-id="element-631" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-24 bg-offwhite" data-id="element-632">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-633">
          <AnimatedSection className="text-center mb-16" data-id="element-634">
            <SectionHeading title="Onze Expertise" align="center" className="mx-auto" data-id="element-635" />
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10" data-id="element-636">
            {[{
            icon: <Target size={24} data-id="element-637" />,
            title: 'Waarderingsmethoden',
            desc: 'Diepgaande kennis van DCF, multiples en intrinsieke waarde berekeningen, toegepast op de specifieke situatie van uw MKB-onderneming.'
          }, {
            icon: <BookOpen size={24} data-id="element-638" />,
            title: 'Sectorkennis',
            desc: "We begrijpen de specifieke waardedrivers en risico's in diverse sectoren, van IT en zakelijke dienstverlening tot productie en logistiek."
          }, {
            icon: <Shield size={24} data-id="element-639" />,
            title: 'Juridische Context',
            desc: 'Ervaring met waarderingen in juridische context, zoals bij echtscheidingen of aandeelhoudersgeschillen, waarbij onafhankelijkheid cruciaal is.'
          }, {
            icon: <Handshake size={24} data-id="element-640" />,
            title: 'Strategisch Advies',
            desc: 'We leveren niet alleen een getal, maar adviseren u ook over hoe u de waarde van uw onderneming in de toekomst kunt optimaliseren.'
          }].map((item, i) => <AnimatedSection key={i} delay={i * 0.1} className="relative pl-6 border-l-2 border-warmgray/60 py-2" data-id="element-641">
                <div className="absolute -left-[17px] top-2 w-8 h-8 bg-charcoal text-white rounded-full flex items-center justify-center" data-id="element-642">
                  <span className="scale-[0.7]" data-id="element-643">{item.icon}</span>
                </div>
                <h3 className="text-lg font-medium text-charcoal mb-2" data-id="element-644">
                  {item.title}
                </h3>
                <p className="text-charcoal/60 text-sm leading-relaxed" data-id="element-645">
                  {item.desc}
                </p>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" data-id="element-646">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-647">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-648">
            <AnimatedSection data-id="element-649">
              <h2 className="text-3xl font-light text-charcoal mb-8" data-id="element-650">
                Waarom kiezen voor ons?
              </h2>
              <ul className="space-y-6" data-id="element-651">
                {[{
                title: '100% Onafhankelijk',
                desc: 'Wij hebben geen dubbele belangen. Ons enige doel is het leveren van een objectief en eerlijk oordeel.'
              }, {
                title: 'Gecertificeerde Experts',
                desc: 'Ons netwerk bestaat uit hoogopgeleide professionals, waaronder Register Valuators (RV).'
              }, {
                title: 'Transparante Werkwijze',
                desc: 'Heldere afspraken vooraf over doorlooptijd, kosten en het verwachte eindresultaat.'
              }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-652">
                    <CheckCircle2 className="text-accent shrink-0 mt-1" size={24} data-id="element-653" />
                    <div data-id="element-654">
                      <h4 className="font-medium text-charcoal text-lg" data-id="element-655">
                        {item.title}
                      </h4>
                      <p className="text-charcoal/70 mt-1" data-id="element-656">{item.desc}</p>
                    </div>
                  </li>)}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.2} className="bg-charcoal text-white p-10 rounded-3xl flex flex-col justify-center" data-id="element-657">
              <h3 className="text-2xl font-light mb-4" data-id="element-658">Ons Netwerk</h3>
              <p className="text-warmgray/80 mb-8 leading-relaxed" data-id="element-659">
                Wij werken met een landelijk netwerk van onafhankelijke
                adviseurs. Hierdoor kunnen we altijd de juiste specialist
                koppelen aan uw specifieke vraagstuk en sector.
              </p>
              <Link to="/adviseurs" className="inline-block bg-accent hover:bg-accent-hover text-white px-6 py-3 rounded-full text-sm font-medium transition-all shadow-subtle self-start" data-id="element-660">
                Bekijk onze adviseurs
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <CTASection data-id="element-661" />
    </div>;
}